import {Group} from '@app/core/models/group.model';
import {User} from '@app/core/models/user.model';
import {RDate} from '@app/core/models/rDate.model';
import {ILanguage, InternationalizationService} from '@app/core/services/internationalization.service';
import {BaseModel} from '@app/core/models/base.model';
import {RoundingRule, RoundingRuleMethod, RoundingRuleType} from '@app/core/models/roundingRule.model';
import {IQOSelectData} from '@quickorder/angular-web-lib/lib/types/interfaces/q-o-select-data';

export interface IPunchArea {
	id?: number;
	lat: number;
	lng: number;
	name: string;
	radius: number;
	created_at?: string;
	updated_at?: string;
	deleted_at?: string | null;
}

export class Department extends BaseModel {
	public id: number;
	public can_user_punch: boolean;
	public department_name: string;
	public punch_clock_enabled: boolean;
	public punch_user_enabled: boolean;
	public punch_user_pin_enabled: boolean;
	public punch_clock_inside_area_required: boolean;
	public groups: Group[] = [];
	public users: User[] = [];
	public payment_number: string;
	public created_at: RDate;
	public updated_at: RDate;
	public address: string;
	public city: string;
	public postal_code: string;
	public email: string;
	public phone_ext: string;
	public phone_number: string;
	public CVR: number;
	public punch_password_set: boolean;
	public punch_areas: IPunchArea[] = [];
	public rounding_rules: RoundingRule[] = [];
	public rounding_rules_ordered: {
		BEFORE_SCHEDULED_START?: RoundingRule,
		AFTER_SCHEDULED_START?: RoundingRule,
		BEFORE_SCHEDULED_END?: RoundingRule,
		AFTER_SCHEDULED_END?: RoundingRule,
	} = {};
	public payed_pause: boolean;
	public permanently_employed_supplements_enabled: boolean;
	public punch_password: string;
	public shift_change_notification_interval: number;
	public qoGroups?: IQOSelectData<Group>[] = [];
	public language: ILanguage;
	public salary_expenses_additional_percentage?: number;

	constructor(tenantTZ: string, interService: InternationalizationService) {
		super(tenantTZ, interService);
	}

	public getName(): string {
		return this.department_name;
	}

	public getPaymentNumber(): string {
		return 'payment_number';
	}

	public setOrderedRoundingRules(): void {
		this.rounding_rules_ordered = {};
		Object.values(RoundingRuleType).forEach(type => this.rounding_rules_ordered[type] = new RoundingRule(this.tenantTZ, this.interService).deserialize({
			type,
			method: RoundingRuleMethod.NO_ROUND_PUNCHED,
			minutes_of_rounding: null,
		}));
		this.rounding_rules.forEach(rule => {
			this.rounding_rules_ordered[rule.type] = rule;
		});
	}

	public deserialize(input: any): this {
		Object.assign(this, input);
		if (!this.groups || input.groups) {
			this.groups = [];
		}
		if (!this.users || input.users) {
			this.users = [];
		}
		if (!this.rounding_rules || input.rounding_rules) {
			this.rounding_rules = [];
		}
		if (input.groups) {
			this.groups = input.groups.map(groupInput => new Group(this.tenantTZ, this.interService).deserialize(groupInput));
		}
		if (input.users) {
			this.users = input.users.map(userInput => new User(this.tenantTZ, this.interService).deserialize(userInput));
		}
		if (input.rounding_rules) {
			this.rounding_rules = input.rounding_rules.map(rule => new RoundingRule(this.tenantTZ, this.interService).deserialize(rule));
			this.setOrderedRoundingRules();
		}
		if (input.punch_areas) {
			this.punch_areas = input.punch_areas.map(punchArea => ({
				...punchArea,
				lat: parseFloat(punchArea.lat),
				lng: parseFloat(punchArea.lng)
			}));
		}
		if (input.created_at) {
			this.created_at = new RDate(this.tenantTZ, this.interService).deserialize(input.created_at);
		}
		if (input.updated_at) {
			this.updated_at = new RDate(this.tenantTZ, this.interService).deserialize(input.updated_at);
		}

		return this;
	}
}
